import { Button, Heading } from '@appsumo/dorado-react';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';
import useSWR from 'swr';

import { fetchJson } from '~/lib/fetch';

import { useUserStatus } from '~/hooks/userStatus';
import { QuestionPageProps } from '~/lib/discussions/types';
import { PDPContext } from '~/lib/product/context';
import { useOneTap } from '~/lib/script';
import { firstSentence } from '~/lib/util';

import {
  ActionsV2,
  ButtonsV2,
  ChildrenV2,
  UserMetaV2,
} from '~/components/discussions';
import DiscussionSidebar from '~/components/discussions/DiscussionSidebar';
import StatusTags from '~/components/discussions/DiscussionStatus';
import { QuestionSeo } from '~/components/seo';
import { Breadcrumb, Breadcrumbs } from '../ui';
import QuestionCard from './card/QuestionCard';
import CommentList from './sidebar/CommentList';

export const QuestionPageComponent = ({
  deal,
  discussion: fallbackData,
  discussionId,
  useQuestions2_5UI = false,
}: QuestionPageProps) => {
  useOneTap();

  // API V2
  const url = `/api/v2/deals/${deal.id}/questions/${discussionId}/`;

  const { data: commentData, mutate } = useSWR(url, fetchJson, {
    fallbackData: fallbackData,
  });
  const comments = commentData?.comments ? commentData.comments : commentData;
  const question = comments[0];

  const { userStatus } = useUserStatus(deal.id);
  const { user } = question;

  const questionTitle = question.title || question.comment;
  const truncatedTitle = firstSentence({
    text: questionTitle,
    maxLength: 100,
    showEllipsis: true,
  });

  const items: Breadcrumb[] = useMemo(
    () => [
      { href: `/products/${deal.slug}/`, label: deal.public_name },
      {
        href: `/products/${deal.slug}/questions/`,
        label: `${deal.public_name} Questions`,
      },
      { label: truncatedTitle },
    ],
    [deal.public_name, deal.slug, truncatedTitle],
  );

  return (
    <>
      <QuestionSeo question={question} deal={deal} />
      <PDPContext.Provider value={{ deal }}>
        <div className="mx-auto mb-8 w-full max-w-7xl grow space-y-4 px-8 md:my-12">
          <Breadcrumbs items={items} className="mt-6 md:mb-4 lg:mb-6 xl:px-8" />

          {/* Mobile deal header */}
          <div className="-mx-8 mb-4 flex flex-wrap justify-between gap-x-4 border-b p-4 px-8 sm:hidden">
            <h3 className="font-header text-2xl font-semibold">
              {deal.public_name}
            </h3>
            <Link
              href={`/products/${deal.slug}/`}
              className="flex items-center text-blue-600"
            >
              Product details
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                className="ml-2"
              />
            </Link>
          </div>
          {/* End Mobile deal header */}

          <article className="mx-auto flex max-w-6xl gap-x-8 max-sm:flex-col">
            <div className="grow">
              {!useQuestions2_5UI && userStatus?.user_moderator && (
                <StatusTags
                  isApproved={question.approved}
                  isResolved={question.resolved}
                  discussionStatus={question.status?.toLowerCase()}
                />
              )}

              {!useQuestions2_5UI && (
                <header className="mb-4">
                  <Heading.H1 className="max-sm:text-2xl">
                    {truncatedTitle}
                  </Heading.H1>
                </header>
              )}

              <main>
                {useQuestions2_5UI && (
                  <QuestionCard
                    question={question}
                    mutateComment={mutate}
                    partnersIds={deal.partner_user_ids ?? []}
                  />
                )}

                {!useQuestions2_5UI && (
                  <>
                    {truncatedTitle !== questionTitle && (
                      <p className="leading-relaxed">{questionTitle}</p>
                    )}
                    <div className="mt-4 flex gap-x-4">
                      <Link href={`/profile/${user.username}/`} target="_blank">
                        <Image
                          src={user.avatar}
                          alt={user.username}
                          width={70}
                          height={70}
                          unoptimized
                          className="h-[70px] w-[70px] rounded-full max-sm:h-[40px] max-sm:w-[40px]"
                        />
                      </Link>
                      <UserMetaV2
                        user={question.user}
                        posted={question.created}
                        purchased={question.purchased}
                      />
                    </div>
                    <>
                      <ButtonsV2 comment={question} mutateComment={mutate} />
                      <ActionsV2
                        comment={question}
                        mutateComment={mutate}
                        moderator={userStatus?.user_moderator}
                      />
                      {question.children && question.children.length > 0 && (
                        <div className="pt-10">
                          <ChildrenV2
                            root={question}
                            comments={question.children}
                            mutateComment={mutate}
                            moderator={userStatus?.user_moderator}
                          />
                        </div>
                      )}
                    </>
                  </>
                )}
              </main>
            </div>

            <div className={useQuestions2_5UI ? 'hidden lg:block' : ''}>
              <DiscussionSidebar deal={deal} />
            </div>
          </article>

          {useQuestions2_5UI && (
            <div className="space-y-4 lg:hidden">
              <CommentList
                title="Related questions"
                prefix={`/products/${deal.slug}/`}
                comments={deal.top_5_questions}
              />

              <Button
                href={`/products/${deal.slug}/`}
                tertiary
                className="block text-center"
              >
                View product details
              </Button>
            </div>
          )}
        </div>
      </PDPContext.Provider>
    </>
  );
};
